
import { Json } from "@/integrations/supabase/types";

export type SubscriptionTier = 'free' | 'starter' | 'pro' | 'unlimited';

export interface Subscription {
  id: string;
  tier: SubscriptionTier;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
  status: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface LeadImport {
  id: string;
  monthYear: string;
  count: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  tier: SubscriptionTier;
  price: number;
  priceDisplay: string;
  description: string;
  features: string[];
  leadLimit: number;
  recommended?: boolean;
}

// Define subscription plans
export const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    tier: 'free',
    price: 0,
    priceDisplay: '€0',
    description: 'Basic features for small businesses',
    features: [
      '20 leads per month',
      'Advanced lead details',
      'Email template access',
    ],
    leadLimit: 20,
  },
  {
    id: 'starter',
    name: 'Starter',
    tier: 'starter',
    price: 10,
    priceDisplay: '€10',
    description: 'Everything you need to grow your business',
    features: [
      '100 leads per month',
      'Advanced lead details',
      'Email and call templates',
      'Lead status tracking',
    ],
    leadLimit: 100,
  },
  {
    id: 'pro',
    name: 'Pro',
    tier: 'pro',
    price: 30,
    priceDisplay: '€30',
    description: 'Professional tools for serious businesses',
    features: [
      '800 leads per month',
      'Complete lead management',
      'Advanced analytics',
      'Priority support',
    ],
    leadLimit: 800,
    recommended: true,
  },
  {
    id: 'unlimited',
    name: 'Unlimited',
    tier: 'unlimited',
    price: 199,
    priceDisplay: '€199',
    description: 'Unlimited leads for enterprise growth',
    features: [
      'Unlimited leads per month',
      'Complete lead management',
      'Advanced analytics',
      'Priority support',
      'Dedicated account manager',
      'Custom integrations',
    ],
    leadLimit: Infinity,
    recommended: false,
  },
];

// Get subscription plan details by tier
export const getSubscriptionPlanByTier = (tier: SubscriptionTier): SubscriptionPlan => {
  return subscriptionPlans.find(plan => plan.tier === tier) || subscriptionPlans[0];
};

// Convert database plan to frontend plan format
export const convertDBPlanToSubscriptionPlan = (dbPlan: any): SubscriptionPlan => ({
  id: dbPlan.tier,
  name: dbPlan.name,
  tier: dbPlan.tier,
  price: dbPlan.price / 100, // Convert from cents
  priceDisplay: dbPlan.price_display,
  description: dbPlan.description || '',
  features: dbPlan.features || [],
  leadLimit: dbPlan.lead_limit,
  recommended: dbPlan.is_recommended
});
